// SA POV — used inside AddClient and EditClient (SA manages facilities on behalf of a client)
import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus } from 'react-feather';
import { TextField } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import { ToggleButton } from '@/shared/components/ToggleButton';
import CreateModal from '@/shared/layout/createLayout/CreateModal';
import { AddEquipmentForm } from '../AddEquipmentForm';
import { EquipmentList } from '../EquipmentList/EquipmentList';
import type { EquipmentListItem } from '../../api/listEquipmentApiService';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import {
  useAddClientStore,
  type AddClientStoreState,
} from '@/epics/clientManagement/store/addClientStore';
import type { EditingEquipmentState } from '../../hooks/useAddFacility';

import type {
  FacilityEntry,
  FacilityEntryErrors,
} from '../../types/addFacilityType';
import type {
  EquipmentEntry,
  EquipmentEntryErrors,
} from '../../types/addEquipmentType';

// Shimmer skeleton component for loading state
const FieldSkeleton = () => (
  <div className="h-14 bg-gray-300 rounded-lg animate-pulse" />
);

interface AddFacilityFormProps {
  facility: FacilityEntry;
  errors: FacilityEntryErrors;
  isSubmitting: boolean;
  isFacilitySubmitting: boolean;
  clientId: string;
  serverFacilityId: string | null;
  equipmentRefreshKey: number;
  equipmentList: EquipmentEntry[];
  equipmentErrors: EquipmentEntryErrors[];
  uploadProgressList: Record<string, number>[];
  failedUploadFilesList: string[][];
  equipmentServerIds: (string | undefined)[];
  equipmentConflicts?: Record<string, string>;
  onEquipmentConflictsClear?: () => void;

  // Edit mode props
  mode?: 'add' | 'edit';
  isLoading?: boolean;
  facilityStatus?: 'active' | 'inactive' | 'cascaded';
  isStatusUpdating?: boolean;
  onStatusToggle?: (isActive: boolean) => void;

  onFacilityFieldChange: (
    field: keyof Pick<
      FacilityEntry,
      'name' | 'address1' | 'address2' | 'city' | 'state' | 'zip'
    >,
    value: string
  ) => void;
  onNipChange: (raw: string) => void;
  onPhoneChange: (raw: string) => void;
  onAddEquipment: () => void;
  onEquipmentFieldChange: (
    equipId: string,
    field: keyof Pick<
      EquipmentEntry,
      'name' | 'description' | 'serialNumber' | 'modelName'
    >,
    value: string
  ) => void;
  onEquipmentCategoryChange?: (equipId: string, value: string) => void;
  onEquipmentDateOfProductionChange?: (
    equipId: string,
    date: Date | undefined
  ) => void;
  onEquipmentImageChange: (equipId: string, files: File[]) => void;
  onEquipmentImageError: (equipId: string, message: string) => void;
  onEquipmentDocsChange: (equipId: string, files: File[]) => void;
  onEquipmentDocsError: (equipId: string, message: string) => void;
  onEquipmentRetry: (equipId: string, filename: string) => void;
  onEquipmentRemove: (equipId: string) => void;
  onEquipmentReset: (equipId: string) => void;
  onEquipmentValidate: (equipId: string) => void;
  onEditEquipment?: (item: EquipmentListItem) => void;
  editingEquipment?: EditingEquipmentState | undefined;
  onEquipmentEditFieldChange?:
    | ((
        field: keyof Pick<
          EquipmentEntry,
          'name' | 'description' | 'serialNumber' | 'modelName'
        >,
        value: string
      ) => void)
    | undefined;
  onEquipmentEditCategoryChange?: ((value: string) => void) | undefined;
  onEquipmentEditDateOfProductionChange?:
    | ((date: Date | undefined) => void)
    | undefined;
  onEquipmentEditImageChange?: ((files: File[]) => void) | undefined;
  onEquipmentEditImageError?: ((message: string) => void) | undefined;
  onEquipmentEditDocsChange?: ((files: File[]) => void) | undefined;
  onEquipmentEditDocsError?: ((message: string) => void) | undefined;
  onEquipmentEditExistingImageDelete?: (() => void) | undefined;
  onEquipmentEditExistingDocDelete?: ((path: string) => void) | undefined;
  onEquipmentEditRetry?: ((filename: string) => void) | undefined;
  onEquipmentEditSubmit?: (() => void) | undefined;
  onEquipmentEditCancel?: (() => void) | undefined;
  onEquipmentAccessDenied?: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  onClearFacility: () => void;
  /** When true, the Cancel button is enabled even on an empty form (e.g. when
   *  existing facilities are present and the user can return to the list). */
  canCancel?: boolean;
}

export const AddFacilityForm = ({
  facility,
  errors,
  isSubmitting,
  isFacilitySubmitting,
  clientId,
  serverFacilityId,
  equipmentRefreshKey,
  equipmentList,
  equipmentErrors,
  uploadProgressList,
  failedUploadFilesList,
  mode = 'add',
  isLoading = false,
  facilityStatus,
  isStatusUpdating = false,
  onStatusToggle,
  onFacilityFieldChange,
  onNipChange,
  onPhoneChange,
  onEquipmentFieldChange,
  onEquipmentCategoryChange,
  onEquipmentDateOfProductionChange,
  onEquipmentImageChange,
  onEquipmentImageError,
  onEquipmentDocsChange,
  onEquipmentDocsError,
  onEquipmentRetry,
  onEquipmentRemove,
  onEquipmentReset,
  onEquipmentValidate,
  onEditEquipment,
  editingEquipment,
  onEquipmentEditFieldChange,
  onEquipmentEditCategoryChange,
  onEquipmentEditDateOfProductionChange,
  onEquipmentEditImageChange,
  onEquipmentEditImageError,
  onEquipmentEditDocsChange,
  onEquipmentEditDocsError,
  onEquipmentEditExistingImageDelete,
  onEquipmentEditExistingDocDelete,
  onEquipmentEditRetry,
  onEquipmentEditSubmit,
  onEquipmentEditCancel,
  onEquipmentAccessDenied,
  onAddEquipment,
  onSubmit,
  onCancel,
  onClearFacility,
  canCancel = false,
  equipmentServerIds,
  equipmentConflicts,
  onEquipmentConflictsClear,
}: AddFacilityFormProps) => {
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [apiEquipmentCount, setApiEquipmentCount] = useState<number | null>(
    null
  );
  const submitRequestedRef = useRef(false);
  const prevEquipmentServerIdsRef = useRef<(string | undefined)[]>([]);
  const autoAddedEquipmentRef = useRef(false);
  // Stable ref so the autoAdd effect never needs onAddEquipment in its deps.
  const onAddEquipmentRef = useRef(onAddEquipment);
  onAddEquipmentRef.current = onAddEquipment;
  const onEquipmentConflictsClearRef = useRef(onEquipmentConflictsClear);
  onEquipmentConflictsClearRef.current = onEquipmentConflictsClear;
  // Stable so EquipmentList's fetch effect dep on onTotalCountChange never changes.
  const handleEquipmentTotalCountChange = useCallback((count: number) => {
    setApiEquipmentCount(count);
    onEquipmentConflictsClearRef.current?.();
  }, []);
  const setHasPendingForm = useAddClientStore(
    (s: AddClientStoreState) => s.setHasPendingForm
  );
  const setEquipmentServerIds = useAddClientStore(
    (s: AddClientStoreState) => s.setEquipmentServerIds
  );

  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    submitRequestedRef.current = false;

    const fieldOrder: (keyof FacilityEntryErrors)[] = [
      'name',
      'phone',
      'address1',
      'city',
      'state',
      'zip',
      'nip',
    ];
    const firstErrorField = fieldOrder.find((f) => Boolean(errors[f]));
    if (!firstErrorField) {
      return;
    }

    document
      .querySelector(`[data-field="facility-${firstErrorField}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errors]);

  const handleSubmit = () => {
    submitRequestedRef.current = true;
    onSubmit();
  };

  const isFacilityDirty =
    facility.name !== '' ||
    facility.nip !== '' ||
    facility.phone !== '' ||
    facility.address1 !== '' ||
    facility.address2 !== '' ||
    facility.city !== '' ||
    facility.state !== '' ||
    facility.zip !== '';

  const isAnyEquipmentDirty = equipmentList.some(
    (e, i) =>
      !equipmentServerIds[i] &&
      (e.name !== '' ||
        e.description !== '' ||
        e.category !== '' ||
        e.serialNumber !== '' ||
        e.modelName !== '' ||
        e.dateOfProduction !== undefined ||
        e.imageFile !== undefined ||
        e.docFiles.length > 0)
  );

  const isDirty = isFacilityDirty;

  const handleCancel = () => {
    if (isDirty) {
      setConfirmClearOpen(true);
    } else {
      onCancel();
    }
  };

  const submittedCount = equipmentServerIds.filter(Boolean).length;
  const hasPendingForm = equipmentList.some((_, i) => !equipmentServerIds[i]);

  useEffect(() => {
    if (!serverFacilityId) {
      return;
    }
    setHasPendingForm(serverFacilityId, hasPendingForm);
  }, [serverFacilityId, hasPendingForm, setHasPendingForm]);

  useEffect(() => {
    if (!serverFacilityId) {
      return;
    }
    const prev = prevEquipmentServerIdsRef.current;
    const hasChanged =
      prev.length !== equipmentServerIds.length ||
      prev.some((id, i) => id !== equipmentServerIds[i]);
    if (!hasChanged) {
      return;
    }
    prevEquipmentServerIdsRef.current = equipmentServerIds;
    setEquipmentServerIds(serverFacilityId, equipmentServerIds);
  }, [serverFacilityId, equipmentServerIds, setEquipmentServerIds]);

  // In edit mode with no equipment, auto-add empty form to show AddEquipmentForm (only once)
  // Wait until EquipmentList has loaded from API and confirmed no equipment (apiEquipmentCount is not null)
  useEffect(() => {
    if (
      mode === 'edit' &&
      !isLoading &&
      apiEquipmentCount !== null && // API has fetched equipment count
      apiEquipmentCount === 0 && // Confirmed no equipment from API
      !hasPendingForm &&
      equipmentList.length === 0 &&
      !autoAddedEquipmentRef.current
    ) {
      autoAddedEquipmentRef.current = true;
      onAddEquipmentRef.current();
    }
  }, [
    mode,
    isLoading,
    apiEquipmentCount,
    hasPendingForm,
    equipmentList.length,
  ]);

  return (
    <div className="flex flex-col gap-4 pt-3">
      <CreateModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirmCancel={() => {
          setConfirmClearOpen(false);
          onClearFacility();
          onCancel();
        }}
      />
      {/* <p className="text-[18px] font-light text-on-surface-variant py-[7px]">
        {FACILITY_LABELS.FACILITY_TITLE} (0)
      </p> */}
      <div className="border border-[#DCDDE0] p-4 rounded-[12px] flex flex-col">
        {/* Basic details */}
        <div className="flex items-center justify-between">
          <p className="text-[18px] font-normal text-neutral-10">
            {FACILITY_LABELS.FACILITY_DETAILS_TITLE}
          </p>
          {mode === 'edit' &&
            facilityStatus !== undefined &&
            onStatusToggle && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-on-surface-variant">Status:</span>
                <div
                  role="button"
                  tabIndex={
                    isStatusUpdating || facilityStatus === 'cascaded' ? -1 : 0
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isStatusUpdating && facilityStatus !== 'cascaded') {
                      onStatusToggle(facilityStatus !== 'active');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      (e.key === 'Enter' || e.key === ' ') &&
                      !isStatusUpdating &&
                      facilityStatus !== 'cascaded'
                    ) {
                      e.preventDefault();
                      onStatusToggle(facilityStatus !== 'active');
                    }
                  }}
                  style={{
                    cursor:
                      isStatusUpdating || facilityStatus === 'cascaded'
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: isStatusUpdating ? 0.6 : 1,
                  }}
                >
                  <ToggleButton
                    checked={facilityStatus === 'active'}
                    disabled={isStatusUpdating || facilityStatus === 'cascaded'}
                    onChange={() => {}}
                    onText={FACILITY_LABELS.STATUS_ACTIVE}
                    offText={FACILITY_LABELS.STATUS_INACTIVE}
                    className="pointer-events-none"
                  />
                </div>
              </div>
            )}
        </div>
        <div className="flex flex-col">
          <div className="flex flex-col gap-[32px]">
            {/* Facility info section (edit mode only) */}
            {mode === 'edit' && (
              <div className="flex flex-col gap-4">
                <p className="text-[18px] font-normal text-on-surface-variant mt-6">
                  {FACILITY_LABELS.SECTION_BASIC_DETAILS}
                </p>
                <div className="flex gap-4">
                  <div className="flex-1" data-field="facility-name">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_EDIT_NAME}
                        autoTrimMaxLength={200}
                        value={facility.name}
                        onChange={(e) =>
                          onFacilityFieldChange(
                            'name',
                            e.target.value.trimStart()
                          )
                        }
                        error={Boolean(errors.name)}
                        errorMessage={errors.name}
                        disabled={isSubmitting}
                        dataTestId="facility-name-edit"
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label="Facility ID"
                        disabled
                        value={facility.facilityId || ''}
                        dataTestId="facility-id"
                      />
                    )}
                  </div>
                  <div className="flex-1" data-field="facility-phone">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_PHONE}
                        dataTestId={`facility-phone`}
                        type="tel"
                        maxLength={9}
                        value={facility.phone}
                        onChange={(e) => onPhoneChange(e.target.value)}
                        error={Boolean(errors.phone)}
                        errorMessage={errors.phone}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Show section header and fields only in add mode */}
            {mode !== 'edit' && (
              <div className="flex flex-col gap-4">
                <p className="text-[18px] font-normal text-on-surface-variant mt-6">
                  {FACILITY_LABELS.SECTION_BASIC_DETAILS}
                </p>

                {/* Row 1: Name (1/3 width) */}
                <div className="flex gap-4">
                  <div className="flex-1" data-field="facility-name">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_NAME}
                        dataTestId={`facility-name`}
                        autoTrimMaxLength={200}
                        value={facility.name}
                        onChange={(e) =>
                          onFacilityFieldChange(
                            'name',
                            e.target.value.trimStart()
                          )
                        }
                        error={Boolean(errors.name)}
                        errorMessage={errors.name}
                        disabled={isSubmitting}
                        autoFocus
                      />
                    )}
                  </div>
                  <div className="flex-1" data-field="facility-phone">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_PHONE}
                        dataTestId={`facility-phone`}
                        type="tel"
                        maxLength={9}
                        value={facility.phone}
                        onChange={(e) => onPhoneChange(e.target.value)}
                        error={Boolean(errors.phone)}
                        errorMessage={errors.phone}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                  <div className="flex-1" />
                </div>
              </div>
            )}

            <div className="border-b-2 border-[#DCDDE0]" />

            {/* Address section */}
            <div className="flex flex-col gap-4">
              <p className="text-[18px] text-on-surface-variant">
                {FACILITY_LABELS.SECTION_ADDRESS}
              </p>
              <div className="flex flex-col gap-[32px]">
                {/* Row: Address1 | Address2 | City */}
                <div className="flex gap-4">
                  <div className="flex-1" data-field="facility-address1">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_ADDRESS1}
                        dataTestId={`facility-address1`}
                        autoTrimMaxLength={200}
                        value={facility.address1}
                        onChange={(e) =>
                          onFacilityFieldChange(
                            'address1',
                            e.target.value.trimStart()
                          )
                        }
                        error={Boolean(errors.address1)}
                        errorMessage={errors.address1}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={
                          <>
                            {FACILITY_LABELS.FIELD_ADDRESS2}
                            <span className="text-bodySmall font-normal">
                              {' '}
                              {FACILITY_LABELS.FIELD_OPTIONAL}
                            </span>
                          </>
                        }
                        dataTestId={`facility-address2`}
                        autoTrimMaxLength={200}
                        value={facility.address2}
                        onChange={(e) =>
                          onFacilityFieldChange(
                            'address2',
                            e.target.value.trimStart()
                          )
                        }
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                  <div className="flex-1" data-field="facility-city">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_CITY}
                        dataTestId={`facility-city`}
                        autoTrimMaxLength={200}
                        value={facility.city}
                        onChange={(e) =>
                          onFacilityFieldChange(
                            'city',
                            e.target.value.trimStart()
                          )
                        }
                        error={Boolean(errors.city)}
                        errorMessage={errors.city}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                </div>

                {/* Row: State | Zip | Country */}
                <div className="flex gap-4">
                  <div className="flex-1" data-field="facility-state">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_STATE}
                        dataTestId={`facility-state`}
                        autoTrimMaxLength={200}
                        value={facility.state}
                        onChange={(e) =>
                          onFacilityFieldChange(
                            'state',
                            e.target.value.trimStart()
                          )
                        }
                        error={Boolean(errors.state)}
                        errorMessage={errors.state}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                  <div className="flex-1" data-field="facility-zip">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={
                          <>
                            {FACILITY_LABELS.FIELD_ZIP}
                            <span className="text-bodySmall font-normal">
                              {' '}
                              {FACILITY_LABELS.FIELD_OPTIONAL}
                            </span>
                          </>
                        }
                        dataTestId={`facility-zip`}
                        maxLength={6}
                        value={facility.zip}
                        onChange={(e) =>
                          onFacilityFieldChange(
                            'zip',
                            e.target.value.trimStart()
                          )
                        }
                        error={Boolean(errors.zip)}
                        errorMessage={errors.zip}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    {isLoading ? (
                      <FieldSkeleton />
                    ) : (
                      <TextField
                        fullWidth
                        label={FACILITY_LABELS.FIELD_COUNTRY}
                        dataTestId={`facility-country`}
                        value={facility.country}
                        disabled
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0] " />

            {/* Tax section */}
            <div className="flex flex-col gap-4">
              <p className="text-[18px] text-on-surface-variant">
                {FACILITY_LABELS.SECTION_TAX}
              </p>
              <div className="flex gap-4">
                <div className="flex-1" data-field="facility-nip">
                  {isLoading ? (
                    <FieldSkeleton />
                  ) : (
                    <TextField
                      fullWidth
                      label={FACILITY_LABELS.FIELD_NIP}
                      dataTestId={`facility-nip`}
                      autoTrimMaxLength={13}
                      value={facility.nip}
                      onChange={(e) => onNipChange(e.target.value)}
                      error={Boolean(errors.nip)}
                      errorMessage={errors.nip}
                      disabled={isSubmitting}
                    />
                  )}
                </div>
                <div className="flex-[2]" />
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0] " />

            {/* Equipment section */}
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <p className="text-[18px] text-on-surface-variant">
                  {FACILITY_LABELS.SECTION_EQUIPMENT} (
                  {hasPendingForm
                    ? (apiEquipmentCount ?? 0) + submittedCount
                    : (apiEquipmentCount ?? submittedCount)}
                  )
                </p>
                {((apiEquipmentCount ?? 0) > 0 || submittedCount > 0) &&
                  !hasPendingForm &&
                  !editingEquipment && (
                    <button
                      type="button"
                      onClick={onAddEquipment}
                      data-testid="Dodaj aparaturę-button"
                      className="flex items-center gap-2 border border-primary-color-40 text-primary-color-40 rounded-[8px] px-4 py-[10px] text-[14px] font-medium leading-5 cursor-pointer hover:bg-primary-color-40/[0.08] disabled:opacity-38 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      <Plus size={20} />
                      {FACILITY_LABELS.ADD_EQUIPMENT_BUTTON_PRIMARY}
                    </button>
                  )}
              </div>

              <div className="flex flex-col gap-4">
                {/* Edit equipment form — shown when user clicks Edit on a list item */}
                {editingEquipment && (
                  <AddEquipmentForm
                    mode="edit"
                    equipmentId={editingEquipment.equipmentId}
                    {...(editingEquipment.categoryLabel && {
                      initialCategoryLabel: editingEquipment.categoryLabel,
                    })}
                    entry={editingEquipment.entry}
                    errors={editingEquipment.errors}
                    isSubmitting={isSubmitting && !isFacilitySubmitting}
                    uploadProgress={editingEquipment.uploadProgress}
                    failedUploadFiles={editingEquipment.failedUploadFiles}
                    existingImageFiles={
                      editingEquipment.existingImageMeta
                        ? [
                            {
                              filename:
                                editingEquipment.existingImageMeta.filename,
                              fileSize:
                                editingEquipment.existingImageMeta.fileSize,
                              path: editingEquipment.existingImageMeta.path,
                            },
                          ]
                        : undefined
                    }
                    existingDocFiles={editingEquipment.existingDocsMeta.map(
                      (d) => ({
                        filename: d.filename,
                        fileSize: d.fileSize,
                        path: d.path,
                      })
                    )}
                    onExistingImageDelete={onEquipmentEditExistingImageDelete}
                    onExistingDocDelete={onEquipmentEditExistingDocDelete}
                    onFieldChange={(field, value) =>
                      onEquipmentEditFieldChange?.(field, value)
                    }
                    onCategoryChange={(value) =>
                      onEquipmentEditCategoryChange?.(value)
                    }
                    onDateOfProductionChange={(date) =>
                      onEquipmentEditDateOfProductionChange?.(date)
                    }
                    onImageChange={(files) =>
                      onEquipmentEditImageChange?.(files)
                    }
                    onImageError={(msg) => onEquipmentEditImageError?.(msg)}
                    onDocsChange={(files) => onEquipmentEditDocsChange?.(files)}
                    onDocsError={(msg) => onEquipmentEditDocsError?.(msg)}
                    onRetry={onEquipmentEditRetry}
                    onSubmit={() => onEquipmentEditSubmit?.()}
                    canClose
                    onCancel={() => onEquipmentEditCancel?.()}
                  />
                )}

                {/* Normal list + pending add forms — hidden when editing */}
                {!editingEquipment && (
                  <>
                    {/* Show EquipmentList when:
                        1. No pending form is open (!hasPendingForm)
                        2. OR we're still loading equipment count (apiEquipmentCount === null)
                        This ensures list loads to set the count before auto-add form triggers */}
                    {(apiEquipmentCount === null || !hasPendingForm) && (
                      <EquipmentList
                        clientId={clientId}
                        facilityId={serverFacilityId}
                        refreshKey={equipmentRefreshKey}
                        canEdit={true}
                        onEdit={(item) => {
                          onEquipmentConflictsClear?.();
                          onEditEquipment?.(item);
                        }}
                        onTotalCountChange={handleEquipmentTotalCountChange}
                        conflictsByEquipmentId={equipmentConflicts}
                        {...(onEquipmentAccessDenied !== undefined && {
                          onAccessDenied: onEquipmentAccessDenied,
                        })}
                      />
                    )}

                    {/* Pending forms after the list */}
                    {equipmentList
                      .map((entry, i) => ({ entry, i }))
                      .filter(({ i }) => !equipmentServerIds[i])
                      .map(({ entry, i }) => (
                        <AddEquipmentForm
                          key={entry.id}
                          entry={entry}
                          errors={
                            equipmentErrors[i] ?? {
                              name: '',
                              description: '',
                              category: '',
                              serialNumber: '',
                              dateOfProduction: '',
                              modelName: '',
                              imageFile: '',
                              docFiles: '',
                            }
                          }
                          isSubmitting={isSubmitting && !isFacilitySubmitting}
                          uploadProgress={uploadProgressList[i]}
                          failedUploadFiles={failedUploadFilesList[i]}
                          onFieldChange={(field, value) =>
                            onEquipmentFieldChange(entry.id, field, value)
                          }
                          onCategoryChange={(value) =>
                            onEquipmentCategoryChange?.(entry.id, value)
                          }
                          onDateOfProductionChange={(date) =>
                            onEquipmentDateOfProductionChange?.(entry.id, date)
                          }
                          onImageChange={(files) =>
                            onEquipmentImageChange(entry.id, files)
                          }
                          onImageError={(msg) =>
                            onEquipmentImageError(entry.id, msg)
                          }
                          onDocsChange={(files) =>
                            onEquipmentDocsChange(entry.id, files)
                          }
                          onDocsError={(msg) =>
                            onEquipmentDocsError(entry.id, msg)
                          }
                          onRetry={(filename) =>
                            onEquipmentRetry(entry.id, filename)
                          }
                          onSubmit={() => onEquipmentValidate(entry.id)}
                          canClose={
                            submittedCount > 0 || (apiEquipmentCount ?? 0) > 0
                          }
                          onCancel={() =>
                            submittedCount > 0 || (apiEquipmentCount ?? 0) > 0
                              ? onEquipmentRemove(entry.id)
                              : onEquipmentReset(entry.id)
                          }
                        />
                      ))}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="border-b-2 border-[#DCDDE0] -mx-4 mt-6" />

          {/* Action buttons */}
          <div className="flex justify-end gap-4 pt-4">
            <Button
              label={FACILITY_LABELS.CANCEL_BUTTON}
              buttonType="Square"
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting || (!isDirty && !canCancel)}
              className="!bg-transparent disabled:!bg-neutral-10/15 !px-3 !py-1.5 cursor-pointer border border-[#DCDDE0] !text-labelLarge !text-on-surface-variant disabled:!text-neutral-10/[0.38] hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
            />
            <Button
              label={
                isFacilitySubmitting
                  ? mode === 'edit'
                    ? FACILITY_LABELS.EDIT_SUBMIT_BUTTON_LOADING
                    : FACILITY_LABELS.SUBMIT_BUTTON_LOADING
                  : mode === 'edit'
                    ? FACILITY_LABELS.EDIT_SUBMIT_BUTTON
                    : FACILITY_LABELS.SUBMIT_BUTTON
              }
              data-testid="Dodaj placówkę-button"
              type="button"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                isAnyEquipmentDirty ||
                !isFacilityDirty ||
                !!editingEquipment
              }
              className="text-labelLarge cursor-pointer  !px-3 !py-1.5  !rounded-[8px]"
              {...(mode === 'edit' && {
                style: {
                  color: 'var(--Schemes-On-Primary, white)',
                  fontSize: 14,
                  fontFamily: 'Roboto',
                  fontWeight: '500',
                  lineHeight: '20px',
                  letterSpacing: 0.1,
                  wordWrap: 'break-word',
                },
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
